/**
 * InDeep-Net
 * Copyright (C) 2025
 * Authors: Fabien MAREUIL
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { driver } from 'driver.js';
import { applySelectionToMolstar } from './molstar_utils.js';
import 'driver.js/dist/driver.css';

function typeCharacterByCharacter(text, inputElement, delay = 500) {
  let currentText = "";
  let index = 0;
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value'
  ).set;

  const interval = setInterval(() => {
      if (index < text.length) {
          currentText += text[index];
          nativeInputValueSetter.call(inputElement, currentText);
          const event = new Event('change', { bubbles: true });
          inputElement.dispatchEvent(event);
          index++;
      } else {
          clearInterval(interval); 
      }
  }, delay);
}


function animateCursorToElement(targetElement, callback) {
  // Dynamically creates the simulated cursor
  const cursor = document.createElement('div');
  cursor.classList.add('simulated-cursor');
  document.body.appendChild(cursor); // Adds it to the DOM

  // Retrieves the position of the target element
  const rect = targetElement.getBoundingClientRect();
  const targetX = window.scrollX + rect.left + rect.width / 2;
  const targetY = window.scrollY + rect.top + rect.height / 2;

  // Initial position of the cursor (center of the page)
  let cursorX = window.scrollX + window.innerWidth / 2;
  let cursorY = window.scrollY + window.innerHeight / 2;

  // Function to gradually move the cursor
  function moveCursor() {
      const moveSpeed = 15; // Higher value for faster movement

      // Calculates the remaining distance
      const dx = targetX - cursorX;
      const dy = targetY - cursorY;

      // If the cursor is close to the target, stop the movement
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) {
          cursor.style.left = `${targetX}px`;
          cursor.style.top = `${targetY}px`;

          if (typeof callback === 'function') callback(); // Calls the callback if provided

          // Removes the icon from the DOM after animation
          setTimeout(() => cursor.remove(), 1000); // Optional: Removes after 1 second
          return;
      }

      // Updates the positions with a smooth movement
      cursorX += dx / moveSpeed;
      cursorY += dy / moveSpeed;

      // Applies the new positions to the cursor
      cursor.style.left = `${cursorX}px`;
      cursor.style.top = `${cursorY}px`;

      // Continues the movement
      requestAnimationFrame(moveCursor);
  }

  // Starts the animation
  moveCursor();
}


export function GetTour(props) {

    const navigate = useNavigate();
    
    const driverObj = driver({
        showProgress: true,
        allowClose: false,

        steps: [
            {
                popover: {
                  title: 'InDeep<sup>Net</sup> Tour',
                  description: "This short tour will guide you through InDeep<sup>Net</sup>'s interface.",
                },
              },
            
          {
            element: '#controls',
            popover: {
              title: 'Available Data Sources for Analysis',
              description: 'Three types of data sources exist, from: <ul> <li>- A public database</li> <li>- Your computer</li> <li>- A previous InDeep<sup>Net</sup></li> </ul>',
              
          },
          },
          {
            element: '#controls',
            popover: {
              title: 'Example with Public Database Sources',
              description: 'For example, from a public database, you can choose from: <ul> <li>- PDBe</li> <li>- RCSB</li> <li>- AlphaFold</li> </ul>',
              side: "bottom",
              align: "center",
              showButtons: [],
            },
            onHighlightStarted: (controls) => {
                var button = controls.querySelector('button#public');
                animateCursorToElement(button);
              },
            onHighlighted: (controls) => {
                setTimeout(() => {
                    var button = controls.querySelector('button#public');
                    button.click()
                    driverObj.moveNext();
                }, 1500);
            }
          },
          {
            element: '#tourpublic',
            popover: {
              title: 'Example with Public Database Sources',
              description: 'For example, from a public database, you can choose from: <ul> <li>- PDBe</li> <li>- RCSB</li> <li>- AlphaFold</li> </ul>',
              side: "bottom",
              align: "center",
            },
          },
          {
            element: '#tourpublic',
            popover: {
              title: 'Example with PDBe Selection',
              description: 'If you choose PDBe, you will be prompted to enter a PDB ID.',
              showButtons: [],
            },
            onHighlightStarted: (tourpublic) => {
                var button = tourpublic.querySelector('button#PDBe');
                animateCursorToElement(button);
              },
            onHighlighted: (tourpublic) => {
                setTimeout(() => {
                  var button = tourpublic.querySelector('button#PDBe');
                  button.click()
                  driverObj.moveNext();
                }, 1500)
              },

          },

          {
            element: '#databaseselect',
            popover: {
              title: 'Get 1bxl from PDBe',
              description: 'Enter a PDB ID, for example, 1bxl, which is the structure of the Bcl-xL-Bak peptide complex.',
            },
            onHighlightStarted: (databaseselect) => {
                const input = databaseselect.querySelector('input#pdbid');
                typeCharacterByCharacter("1bxl", input, 500);
              },
          },
          {
            element: '#toursubmit',
            popover: {
              title: 'Provide an Email',
              description: 'It is highly recommended to provide an email, but it is not mandatory.',
              onNextClick: () => {
                const tourControlDiv = document.getElementById('tourcontrolbuttons');
                if (tourControlDiv) {
                    const input = tourControlDiv.querySelector('input');
                    if (input) {
                        input.click();
                    }
                }
                driverObj.moveNext();
              }
            },
            onHighlightStarted: () => {
              const tourControlDiv = document.getElementById('tourcontrolbuttons');
              if (tourControlDiv) {
                const input = tourControlDiv.querySelector('input');
                if (input) {
                  animateCursorToElement(input);
                }
              }
            },
          },
          {
            element: '#analysis',
            popover: {
              title: 'Data Visualization',
              description: 'Your data appears in the visualization plugin.',
            },
          },
          {
            element: 'button[title="Toggle Selection Mode"]',
            popover:{
                title: 'Selecting Specific Residues Before Pocket Detection',
                description:'Before detecting pockets, you can select specific residues by activating the selection mode.',
                side: "top",
                align: 'end',
            },
            onHighlightStarted: (button) => {
              animateCursorToElement(button);
            },
          },
          {
            element: '.msp-sequence-wrapper-non-empty',
            popover:{
                title: 'Selecting Specific Residues Before Pocket Detection',
                description:"Once the mode is activated, click on the residues in the sequence to select them.",
            },
            onHighlightStarted: (seq) => {
              animateCursorToElement(seq);
              applySelectionToMolstar('chain A and resi 1-205', props.plugin)
            },
            
          },

          {
            element: '#tourdetectbutton',
            popover: {
              title: 'Detecting Pockets with InDeep',
              description: "To detect pockets with InDeep, click on the 'Detect Pockets' button.",
              onNextClick: () => {
                const tourdetectbutton = document.getElementById('tourdetectbutton');
                if (tourdetectbutton) {
                    tourdetectbutton.click();
                }
                driverObj.moveNext();
              }
            },
            onHighlightStarted: (button) => {
              animateCursorToElement(button);
            },
          },
          {
            element: '#messageJob',
            popover: {
                title: 'Process Status Overview',
                description: "This process may take some time while the status is marked as 'running'. Once the job is complete, the status will change to 'success'.",
              },
          },
          {
            element: '#messageUrl',
            popover: {
                title: 'Results Access and Visualization',
                description: "You will receive a link to access your results later. If it's successful, you can focus on the visualization plugin.",
              },
          },
          {
            element: '#analysis',
            popover: {
                title: 'Visualization',
                description: "The visualization plugin allow to interact with your result and prepare for InDeep<sup>Holo</sup> analyses.",
              },
          },
          {
            element: 'button[title="Toggle Selection Mode"]',
            popover:{
                title: 'Selecting Specific Residues Before InDeep<sup>Holo</sup> calculation',
                description:"Before running InDeep<sup>Holo</sup>, you need to select specific residues to determine their holo-likeness. Activate the selection mode to choose the residues required for your analysis. You can utilize Mol* features by selecting the partner in chain mode, then in 'Add/Union Selection' choose the 'Manipulate Selection' menu, click on 'Surrounding Residues (5 &Aring;) of Selection', and finally deselect the partner (see Mol* documentation:  <a href='https://molstar.org/viewer-docs/'>https://molstar.org/viewer-docs/</a>)",
                side: "top",
                align: 'end',
            },
            onHighlightStarted: (button) => {
              animateCursorToElement(button);
            },
          },
          {
            element: '.msp-sequence-wrapper-non-empty',
            popover:{
                title: 'Selecting Specific Residues Before InDeep<sup>Holo</sup> calculation',
                description:'Once the mode is activated, click on the residues in the sequence to select them.',
            },
            onHighlightStarted: (seq) => {
              animateCursorToElement(seq);
              applySelectionToMolstar('chain A and resi 96-97+100-101+104-105+107-108+111-112+122-122+125-126+129-130+132-132+136-139+142-143+146-146+193-195+199-200+203-204+210-210', props.plugin);
            },
            
          },

          {
            element: '#tourholobutton',
            popover: {
              title: 'Performing InDeep<sup>Holo</sup> Calculation',
              description: "Click on the 'USE INDEEP<sup>HOLO</sup>' button to run the InDeep<sup>Holo</sup> calculation. This step may take a few moments.",
              onNextClick: () => {
                const tourholobutton = document.getElementById('tourholobutton');
                if (tourholobutton) {
                  tourholobutton.click();
                }
                driverObj.moveNext();
              }
            },
            onHighlightStarted: (button) => {
              animateCursorToElement(button);
            },
          },
          {
            element: '#messageJob',
            popover: {
                title: 'Process Status Overview',
                description: "This process may take some time while the status is marked as 'running'. Once the job is complete, the status will change to 'success'.",
              },
          },
          {
            element: '#messageUrl',
            popover: {
                title: 'Accessing Your Results',
                description: "You will receive a link to access your results once the calculation is complete.",
              },
          },
          {
            element: '#tourrefreshholo',
            popover: {
                title: 'Refresh Holo results',
                description: "You can also use the refresh button. If your InDeep<sup>Holo</sup> calculation. The results will appear in the table below once they are available.",
                
              },
              onHighlightStarted: (button) => {
                animateCursorToElement(button);
              },
          },
          {
            element: '#tourholoresult',
            popover: {
                title: 'Holo-likeness Results',
                description: "Your results, including previous ones, are available here. A lower score indicates better holo-likeness for the given residues selection.",
              },
          },
          {
            element: '#analysis',
            popover: {
              title: "You're Now Ready to Use InDeep and InDeep<sup>Holo</sup>!",
              description: 'You have now completed the tutorial and are ready to use our application on your own. Congratulations!',
            
            onNextClick: () => {
                const clear = document.querySelector('#tourclear')
                if (clear) {
                    clear.click();
                    window.scrollTo({
                      top: 0,
                      behavior: 'smooth'
                    });
                }
                driverObj.moveNext();
                navigate('/');
              }
            },


          },

        ],
        
      });

    function startTheMagicShow() {
        navigate('/');
        driverObj.drive();
      }
    return (
      <div>
        <button type="button" className="text-nowrap z-10 select-none rounded hover:bg-blue-800 bg-sky-950 dark:bg-sky-800 hover:dark:bg-blue-800 p-2 text-center align-middle font-sans text-xs font-bold uppercase text-white shadow-md dark:shadow-blue-600/20 shadow-blue-800/20 transition-all hover:shadow-lg dark:hover:shadow-blue-600/40 hover:shadow-blue-800/40 focus:opacity-[0.85] focus:shadow-none active:opacity-[0.85] active:shadow-none peer-placeholder-shown:pointer-events-none peer-placeholder-shown:bg-gray-500 peer-placeholder-shown:opacity-50 peer-placeholder-shown:shadow-none disabled:opacity-50 disabled:bg-sky-950 disabled:shadow-none disabled:hover:bg-sky-950 disabled:hover:dark:bg-sky-800" onClick={startTheMagicShow}>Take a Tour</button>
      </div>
    );
  }